import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOverview, getDashboardSeries } from '../api/client';
import { formatPosts, formatUSD } from '../utils/format';
import type { DashboardSeriesRange } from '../types';

function CardLegend({ children }: { children: React.ReactNode }) {
  // Operator-facing footnote explaining what the metric is and how it's
  // computed. Kept on every widget so the dashboard reads like a glossary
  // — no need to flip back to a doc to remember whether "Active" means
  // "had a report sent" or "had any fetch attempt".
  return (
    <p className="mt-3 border-t border-gray-100 pt-2 text-[11px] leading-snug text-gray-400">
      {children}
    </p>
  );
}

function StatCard({
  label,
  value,
  testid,
  legend
}: {
  label: string;
  value: string | number;
  testid?: string;
  legend?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid={testid}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      {legend && <CardLegend>{legend}</CardLegend>}
    </div>
  );
}

function BarBreakdown({
  title,
  items,
  testid,
  legend
}: {
  title: string;
  items: { label: string; count: number; color: string }[];
  testid?: string;
  legend?: React.ReactNode;
}) {
  const total = items.reduce((sum, i) => sum + i.count, 0);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid={testid}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <div key={item.label}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{item.label}</span>
              <span className="font-medium text-gray-900">{item.count}</span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-gray-100">
              <div
                className={`h-2 rounded-full ${item.color}`}
                style={{ width: total > 0 ? `${(item.count / total) * 100}%` : '0%' }}
              />
            </div>
          </div>
        ))}
      </div>
      {legend && <CardLegend>{legend}</CardLegend>}
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['overview'],
    queryFn: () => getOverview()
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        data-testid="dashboard-error"
      >
        Failed to load overview: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
        <p className="text-xs text-gray-400">
          Generated {new Date(data.generatedAt).toLocaleTimeString()}
        </p>
      </div>

      {/* Top row stat cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total chats"
          value={data.totals.totalChats.toLocaleString()}
          testid="stat-total-chats"
          legend={
            <>
              Distinct Telegram chats that ever installed the bot. Union of{' '}
              <code className="rounded bg-gray-100 px-1">SETTINGS</code> and{' '}
              <code className="rounded bg-gray-100 px-1">LICENSES</code> tables.
            </>
          }
        />
        <StatCard
          label="Active (7d)"
          value={data.totals.activeChats7d.toLocaleString()}
          testid="stat-active-7d"
          legend={
            <>
              Chats with ≥1 report successfully delivered in the last 7 days
              (<code className="rounded bg-gray-100 px-1">REPORTS</code> rows
              with <code className="rounded bg-gray-100 px-1">TIMESTAMP_INMILLIS</code> ≥ now − 7d).
            </>
          }
        />
        <StatCard
          label="Reports last 7d"
          value={formatPosts(data.totals.reportsLast7d)}
          testid="stat-reports-7d"
          legend="Total report deliveries to chats in the last 7 days. Counts every successful send (a chat with a daily schedule contributes ~7)."
        />
        <StatCard
          label="X API spend MTD"
          value={formatUSD(data.totals.xApiSpendMtdUSD)}
          testid="stat-spend-mtd"
          legend={
            <>
              Σ(<code className="rounded bg-gray-100 px-1">STATS.fetched_posts</code>) for the
              current month × <strong>$0.005/post</strong> (X API pay-per-use rate).
            </>
          }
        />
      </div>

      {/* Revenue + bank */}
      <h2 className="mt-8 text-lg font-semibold text-gray-900">Revenue &amp; bank</h2>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="MRR"
          value={formatUSD(data.totals.mrrUSD)}
          testid="stat-mrr"
          legend={
            data.totals.mrrSource === 'estimate' ? (
              <>
                <strong>Estimate — Stripe could not be read.</strong> Falling back
                to count of chats with{' '}
                <code className="rounded bg-gray-100 px-1">LICENSE = PRO</code> and{' '}
                <code className="rounded bg-gray-100 px-1">PAYMENT = SUBSCRIPTION</code>{' '}
                × ${data.totals.mrrMeta?.monthlyPriceUSD ?? 19}/mo. This over-counts
                PRO chats with no live subscription and under-prices each one (the
                fee line item is missing). Check the admin Lambda logs.
              </>
            ) : (
              <>
                Summed from live Stripe subscriptions carrying our price IDs
                (statuses <code className="rounded bg-gray-100 px-1">active</code> and{' '}
                <code className="rounded bg-gray-100 px-1">past_due</code>;
                trials excluded), normalised to a monthly amount.
                {data.totals.mrrMeta?.countedSubscriptions !== undefined && (
                  <> {data.totals.mrrMeta.countedSubscriptions} subscription
                  {data.totals.mrrMeta.countedSubscriptions === 1 ? '' : 's'} counted.</>
                )}
                {data.totals.mrrMeta?.truncated && (
                  <> <strong>Lower bound</strong> — pagination limit reached.</>
                )}
                {data.totals.mrrMeta?.otherCurrencies &&
                  Object.keys(data.totals.mrrMeta.otherCurrencies).length > 0 && (
                    <> <strong>Excludes non-USD subscriptions</strong>:{' '}
                    {Object.entries(data.totals.mrrMeta.otherCurrencies)
                      .map(([c, v]) => `${v} ${c.toUpperCase()}`)
                      .join(', ')}.</>
                  )}
              </>
            )
          }
        />
        <StatCard
          label="PRO-CREDITS bank"
          value={formatPosts(data.totals.proCreditsBank)}
          testid="stat-pro-credits-bank"
          legend={
            <>
              Sum of <code className="rounded bg-gray-100 px-1">REMAINING_CREDITS</code>{' '}
              across all chats with{' '}
              <code className="rounded bg-gray-100 px-1">PAYMENT = CREDITS</code>. The
              total runway of pre-paid post fetches the fleet still has.
            </>
          }
        />
        <StatCard
          label="Active (30d)"
          value={data.totals.activeChats30d.toLocaleString()}
          testid="stat-active-30d"
          legend="Chats with ≥1 report delivered in the last 30 days. Wider window than Active (7d); Active(7d) ÷ Active(30d) is the stickiness ratio."
        />
      </div>

      {/* Breakdowns */}
      <h2 className="mt-8 text-lg font-semibold text-gray-900">Breakdowns</h2>
      <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BarBreakdown
          title="Chats by tier"
          testid="breakdown-by-tier"
          items={[
            { label: 'FREE', count: data.byTier.FREE, color: 'bg-gray-400' },
            { label: 'PRO · credits', count: data.byTier.PRO_CREDITS, color: 'bg-blue-500' },
            { label: 'PRO · subscription', count: data.byTier.PRO_SUB, color: 'bg-primary-600' }
          ]}
          legend={
            <>
              <strong>FREE</strong> = no PRO license (100 free posts/month cap).{' '}
              <strong>PRO · credits</strong> = pay-per-use, decrement
              <code className="ml-1 rounded bg-gray-100 px-1">REMAINING_CREDITS</code>.{' '}
              <strong>PRO · subscription</strong> = monthly Stripe sub;
              past 1 000 posts/mo trigger metered overage.
            </>
          }
        />
        <BarBreakdown
          title="Chats by status"
          testid="breakdown-by-status"
          items={[
            { label: 'Active', count: data.byStatus.active, color: 'bg-emerald-500' },
            { label: 'Silent', count: data.byStatus.silent, color: 'bg-amber-500' },
            { label: 'Disabled', count: data.byStatus.disabled, color: 'bg-gray-400' },
            { label: 'Kicked', count: data.byStatus.kicked, color: 'bg-red-500' }
          ]}
          legend={
            <>
              <strong>Active</strong> = report sent in last 7 d.{' '}
              <strong>Silent</strong> = had a schedule but no report ≥ 7 d
              (broken filter or no posts to report).{' '}
              <strong>Disabled</strong> = no schedule rule.{' '}
              <strong>Kicked</strong> = bot removed from chat.
            </>
          }
        />
      </div>

      {/* Growth — time series over a configurable range */}
      <GrowthSection />
    </div>
  );
}

const RANGE_OPTIONS: DashboardSeriesRange[] = ['7d', '30d', '90d', '1y', 'all'];

function GrowthSection() {
  const [range, setRange] = useState<DashboardSeriesRange>('90d');
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard-series', range],
    queryFn: () => getDashboardSeries(range)
  });

  return (
    <div className="mt-10" data-testid="growth-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-900">Growth &amp; activity</h2>
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1" role="tablist">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt}
              role="tab"
              aria-selected={range === opt}
              onClick={() => setRange(opt)}
              data-testid={`range-${opt}`}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                range === opt
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <div className="h-6 w-6 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load growth: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {data && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SeriesChart
            title="New chats per day"
            testid="chart-new-chats"
            color="bg-emerald-400 hover:bg-emerald-500"
            total={data.totals.newChats}
            unit="installs"
            series={data.newChats}
            legend={
              <>
                <code className="rounded bg-gray-100 px-1">BOT_ADDED</code> events bucketed by UTC day.
                Bot fires this when its membership transitions from absent (left/kicked) to
                present (member/admin) — exactly one per real install.
              </>
            }
          />
          <SeriesChart
            title="Active chats per day"
            testid="chart-active-chats"
            color="bg-primary-400 hover:bg-primary-500"
            total={data.totals.activeChats}
            unit="distinct chats in window"
            series={data.activeChats}
            legend={
              <>
                Distinct <code className="rounded bg-gray-100 px-1">CHAT_ID</code> count per day in
                the <code className="rounded bg-gray-100 px-1">REPORTS</code> table — chats that
                received at least one report on that day.
              </>
            }
          />
        </div>
      )}
    </div>
  );
}

function SeriesChart({
  title,
  testid,
  color,
  total,
  unit,
  series,
  legend
}: {
  title: string;
  testid: string;
  color: string;
  total: number;
  unit: string;
  series: { date: string; count: number }[];
  legend: React.ReactNode;
}) {
  const max = Math.max(1, ...series.map(s => s.count));
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        <p className="text-xs text-gray-500">
          <span className="font-semibold text-gray-900">{total.toLocaleString()}</span> {unit}
        </p>
      </div>
      <div className="mt-4 flex items-end gap-px h-32">
        {series.map(p => (
          <div
            key={p.date}
            className={`flex-1 rounded-t ${color} transition-all`}
            style={{
              height: `${(p.count / max) * 100}%`,
              minHeight: p.count > 0 ? '2px' : '0'
            }}
            title={`${p.date}: ${p.count}`}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-400">
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
      <p className="mt-3 border-t border-gray-100 pt-2 text-[11px] leading-snug text-gray-400">
        {legend}
      </p>
    </div>
  );
}
